/**
 * Error Parser Utility
 *
 * Parses API error responses from multiple formats (new V2 standardized format
 * and legacy formats) into a normalized structure for consistent error handling.
 *
 * Supported Formats:
 * - New V2 format: {type: "...", errors: [{code, detail, attr}]}
 * - Legacy validation error format: {nonFieldErrors: ["error message"]} or {errors: ["error message"]}
 * - Simple format: {message: "..."}
 */

angular.module('azure.util').factory('errorParser', errorParser)

function errorParser() {
   /**
    * Parse an API error response into a normalized format
    *
    * @param {Object} response - The HTTP response object from $http (has status, data, etc.)
    * @returns {Object} Normalized error object with:
    *   - type: 'validation_error' | 'client_error' | 'server_error' | 'unknown'
    *   - message: Main user-facing error message
    *   - code: Error code if applicable
    *   - attr: Field name for validation errors (V2 format only)
    */
   function parseApiResponse(response) {
      if (!response || !response.data) {
         return {
            type: 'unknown',
            message: 'An unexpected error occurred',
         }
      }

      const data = response.data
      const status = response.status || 0

      // New V2 standardized format: {type: "...", errors: [{code, detail, attr}]}
      if (data.type && Array.isArray(data.errors)) {
         return parseV2Format(data)
      }

      // Legacy validation error format: {nonFieldErrors: ["error message"]} or {errors: ["error message"]}
      // Note: errors array check must come after V2 format check since V2 uses {type, errors: [{...}]} with objects
      const errorArray = data.nonFieldErrors || data.errors
      if (errorArray && Array.isArray(errorArray) && errorArray.length > 0 && typeof errorArray[0] === 'string') {
         return parseLegacyValidationErrorMessageFormat(errorArray)
      }

      // Simple message format: {message: "..."}
      return {
         type: determineErrorType(status),
         message: data.message || 'An unexpected error occurred',
      }
   }

   /**
    * Parse the new V2 standardized error format
    */
   function parseV2Format(data) {
      const firstError = data.errors[0] || {}
      let message = firstError.detail || 'An error occurred'

      // Replace "This field" with the formatted attribute name if available
      if (firstError.attr && message.toLowerCase().includes('this field')) {
         const formattedAttr = formatAttributeName(firstError.attr)
         message = message.replace(/This field/gi, formattedAttr)
      }

      return {
         type: data.type,
         message: message,
         code: firstError.code,
         attr: firstError.attr,
      }
   }

   /**
    * Convert snake_case attribute name to Title Case
    * Example: "first_name" -> "First Name"
    */
   function formatAttributeName(attr) {
      return attr
         .split('_')
         .map(function (word) {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
         })
         .join(' ')
   }

   /**
    * Parse legacy validation error format: {nonFieldErrors: ["error message"]} or {errors: ["error message"]}
    */
   function parseLegacyValidationErrorMessageFormat(errorArray) {
      return {
         type: 'validation_error',
         message: errorArray[0],
      }
   }

   /**
    * Determine error type based on HTTP status code
    */
   function determineErrorType(status) {
      if (status >= 500) {
         return 'server_error'
      } else if (status >= 400) {
         return 'client_error'
      } else {
         return 'unknown'
      }
   }

   return {
      parse: parseApiResponse,
   }
}
